<template>
  <section class="page-panel">
    <header class="page-panel__header">
      <h3 class="lead-text ma-0">Pregled svih događaja i rezervacija za ovo vozilo:</h3>
    </header>

    <div class="page-panel__content">
      <v-data-table
        class="table vehicle-usage-history__table"
        :headers="vehicleHistoryHeaders"
        :items="vehicleUsageEntries"
        :items-per-page="-1"
        :loading="isLoadingVehicleUsage"
        :no-data-text="hasVehicleUsageError
          ? 'Događaje i rezervacije za ovo vozilo nije bilo moguće učitati.'
          : 'Nema događaja ni rezervacija za ovo vozilo'"
        item-value="id"
        loading-text="Učitavanje događaja i rezervacija..."
        hide-default-footer
      >
        <template #item.type="{ item: vehicleUsageEntry }">
          {{ vehicleUsageTypeLabels[vehicleUsageEntry.type] }}
        </template>
        <template #item.period="{ item: vehicleUsageEntry }">
          {{ formatDate(vehicleUsageEntry.startDate) }} - {{ formatDate(vehicleUsageEntry.endDate) }}
        </template>
      </v-data-table>
    </div>
  </section>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'

import { formatDate } from '../helpers'
import { apiClient } from '../services/apiClient'
import { showRequestErrorToast } from '../store/appStore'

const props = defineProps({
  vehicleId: {
    type: String,
    required: true,
  },
})

const vehicleUsageEntries = ref([])
const isLoadingVehicleUsage = ref(false)
const hasVehicleUsageError = ref(false)
let latestVehicleUsageRequestId = 0
const vehicleUsageTypeLabels = {
  event: 'Događaj',
  'drivepark-reservation': 'Vozni park - rezervacija',
  'drivepark-rental': 'Vozni park - aktivni najam',
}

const vehicleHistoryHeaders = [
  { title: 'Vrsta', key: 'type', sortable: false, width: '25%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Naziv događaja / korisnika', key: 'name', sortable: false, width: '40%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Razdoblje', key: 'period', sortable: false, width: '35%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
]

watch(() => props.vehicleId, () => {
  vehicleUsageEntries.value = []
  getVehicleUsageAsync()
}, { immediate: true })

onMounted(() => document.addEventListener('visibilitychange', refreshVehicleUsageOnVisibilityChange))
onUnmounted(() => document.removeEventListener('visibilitychange', refreshVehicleUsageOnVisibilityChange))

function refreshVehicleUsageOnVisibilityChange () {
  if (document.visibilityState === 'visible') {
    getVehicleUsageAsync()
  }
}

async function getVehicleUsageAsync () {
  const vehicleId = props.vehicleId
  const requestId = ++latestVehicleUsageRequestId
  isLoadingVehicleUsage.value = true
  hasVehicleUsageError.value = false

  try {
    const fetchedVehicleUsageEntries = await apiClient.getAsync(`/api/vehicles/${vehicleId}/usage`)

    if (requestId === latestVehicleUsageRequestId) {
      vehicleUsageEntries.value = fetchedVehicleUsageEntries
    }
  } catch (error) {
    console.error('Failed to load vehicle usage.', error)

    if (requestId === latestVehicleUsageRequestId) {
      hasVehicleUsageError.value = true
      showRequestErrorToast(error, 'Događaje i rezervacije za ovo vozilo nije bilo moguće učitati.')
    }
  } finally {
    if (requestId === latestVehicleUsageRequestId) {
      isLoadingVehicleUsage.value = false
    }
  }
}
</script>

<style
  scoped
  lang="scss"
>
.vehicle-usage-history {
  &__table {
    --table-actions-width: 35%;
  }
}
</style>
